import { useState } from 'react';
import { useFetch } from '../context/FetchProvider';
import { useVideoPlayer } from '../context/VideoPlayerProvider';
import { track } from '../lib/util/tracking';
import { LearnDataVideo } from '@osmosis/shared/types/learn';
import { UserProductComponentType } from '@osmosis/shared/types/user';
import { TransformedAchievement } from '@osmosis/shared/types/achievement';

import { useUserContext } from '../context/UserContext';
import { usePagesContext } from '../context/PagesProvider';
import throttle from 'lodash/throttle';
import { getAchievementsByType } from '../lib/frontend/achievements';
import {
  PERCENT_THRESHOLD,
  VIDEO_WATCHED_BROADCAST,
  VIDEO_WATCHED_EVENT,
} from '@osmosis/constants';

enum InteractionType {
  start = 'start',
  progress = 'progress',
  finished = 'finished',
}

const interactionTypeMap = {
  start: 'video_start',
  progress: 'video_watched',
  finished: 'video_finished',
};

/**
 * UseVideoTracking.
 */
export default function useVideoTracking() {
  const [isAchievementCalled, setAchievementCalled] = useState(false);
  const [hasTrackedVideoStart, setHasTrackedVideoStart] = useState(false);
  const [hasTrackedVideoFinish, setHasTrackedVideoFinish] = useState(false);
  const { getPlayer, progressState, playing, isReady } = useVideoPlayer();
  const { post } = useFetch();
  const { user, hasProductComponent } = useUserContext();
  const { query } = usePagesContext();
  const language_switcher = hasProductComponent(
    UserProductComponentType.LANGUAGE_SELECTION,
  );

  const currentPosition = progressState.playedSeconds;

  const updateMediaStatus = throttle(
    async (
      video: Pick<
        LearnDataVideo,
        'mediaID' | 'isPrimeOnly' | 'name' | 'breadcrumbsv2'
      >,
      lastTime: number,
      from: string,
      isEmbed: boolean,
      badgeHandler?: (arg: TransformedAchievement[]) => void,
    ): Promise<boolean> => {
      if (!isReady || !playing) return false;

      const { mediaID, isPrimeOnly, name, breadcrumbsv2 } = video;

      let percent = 0;
      const duration = getPlayer()?.getDuration();

      if (currentPosition && duration) {
        percent = Math.floor((currentPosition / duration) * 100);
      }

      if (user) {
        await post('/updateMediaWatchedStatus', {
          percent,
          lastTime,
          mediaNodeID: mediaID,
          from,
        }).then(async () => {
          if (percent >= PERCENT_THRESHOLD) {
            // Send message to other schedules tabs
            const channel = new BroadcastChannel(VIDEO_WATCHED_BROADCAST);
            channel.postMessage({
              type: VIDEO_WATCHED_EVENT,
              payload: { mediaID, percent },
            });
            channel.close();
          }
          if (
            badgeHandler &&
            percent > PERCENT_THRESHOLD &&
            !isAchievementCalled
          ) {
            const achievements = await getAchievementsByType(
              post,
              'videosWatched',
              mediaID,
            );
            if (achievements.length) {
              badgeHandler(achievements);
            }
            setAchievementCalled(true);
          }
        });
      }

      const getInteractionType = () => {
        if (!hasTrackedVideoStart && currentPosition > 2) {
          setHasTrackedVideoStart(true);
          return InteractionType.start;
        }
        if (percent > 80 && !hasTrackedVideoFinish) {
          setHasTrackedVideoFinish(true);
          return InteractionType.finished;
        }
        return InteractionType.progress;
      };

      // get the interaction type
      const type = getInteractionType();

      // convert interaction type to event name
      const eventName = interactionTypeMap[type];

      const breadcrumb =
        breadcrumbsv2?.[0]?.breadcrumbs?.map((breadcrumb) =>
          breadcrumb.toLowerCase().replace(' ', '-'),
        ) || [];

      const organ_system = breadcrumb[breadcrumb.length - 1] || null;
      const source = isEmbed ? 'embed' : 'video-player';
      const data = {
        source,
        mediaID,
        is_prime: !!isPrimeOnly,
        interaction: type,
        concept: name?.toLowerCase() || null,
        language_switcher: language_switcher,
        language: user?.language,
        organ_system,
        breadcrumbs: breadcrumb?.join('|') || null,
        timepoint: Math.floor(currentPosition / 5) * 5,
        percentage: percent / 100,
      };

      await track(eventName, data, ['keen', 'pendo']);

      if (isEmbed) {
        postEmbedTracking(type, data);

        if (query?.lti) await postLtiScore(post, percent, query.concepts);
      }

      return true;
    },
    4000,
    {
      trailing: true,
    },
  );

  return { updateMediaStatus };
}

function postEmbedTracking(type: InteractionType, data: Record<string, any>) {
  const learnURL = window?.location?.pathname?.match(/\/\w+\/(.+)/)?.[1];
  window?.parent?.postMessage(
    {
      type: `video_${type}`,
      timepoint: data.timepoint,
      percentage: data.percentage,
      url: learnURL ? decodeURIComponent(learnURL) : null,
      concept: data.concept,
      mediaID: data.mediaID,
    },
    '*',
  );
}

async function postLtiScore(
  post: (url: string, data?: unknown) => Promise<unknown>,
  percent: number,
  conceptsParam?: string | string[],
) {
  const concepts = Array.isArray(conceptsParam)
    ? conceptsParam
    : conceptsParam?.split(',');

  const shouldSend = !concepts?.length || percent > 80;
  if (!shouldSend) return;

  const score = (percent / 100).toFixed(2);
  await post('/lti/scoreItem', {
    score,
    ...(concepts?.length && { concepts }),
  });
}
